import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { USE_EMULATOR as USE_DATABASE_EMULATOR } from '@angular/fire/compat/database';
import { AngularFirestoreModule, USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';
import { AngularFireFunctionsModule, REGION, USE_EMULATOR as USE_FUNCTIONS_EMULATOR } from '@angular/fire/compat/functions';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { BrowserModule, HAMMER_GESTURE_CONFIG, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { JwtModule } from '@auth0/angular-jwt';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AUTH_EXPIRATION_TIME, FIREBASE_REGION, INACTIVE_PING_CHECK } from './app.constants';
import { setAppInjector } from './core/utils/app.injector';
import { CurrencyConfig } from './core/utils/currency-config';
import { APP_DATE_FORMATS, AppDateAdapter } from './core/utils/date-adapter';
import { HammerConfig } from './core/utils/hammer-config';
import { CopMainModule } from './modules/cop/cop-main/cop-main.module';

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER } from '@angular/material/tooltip';
import { CURRENCY_MASK_CONFIG } from 'ng2-currency-mask';
import { CookieService } from 'ngx-cookie-service';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxSpinnerModule } from 'ngx-spinner';
import { environment } from '../environments/environment';
import { AlertModule } from './core/components/alert/alert.module';
import { SystemWarningsModule } from './core/components/check-version/system-warnings.module';
import { SystemWarningsService } from './core/components/check-version/system-warnings.service';
import { InactiveUserModule } from './core/components/inactive-user/inactive-user.module';
import { CookieDialogModule } from './shared/components/cookie-dialog/cookie-dialog.module';
import { CopHelperModule } from './modules/cop/cop-helper/cop-helper.module';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

export function getToken() {
  return localStorage.getItem('access_token');
}

function updateBaseHref(): () => void {
  return () => {
    const baseTag = document.querySelector('base')
    if (baseTag) {
      baseTag.setAttribute('href', window.location.origin + '/')
    }
  }
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    NgScrollbarModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    CookieDialogModule,
    CopMainModule,
    CopHelperModule,
    AngularFirestoreModule,
    ServiceWorkerModule.register('/ngsw-worker.js', { enabled: environment.production }),
    ToastrModule.forRoot({
      timeOut: 4000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
    AngularFireAuthModule,
    AngularFireFunctionsModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => new TranslateHttpLoader(http, './assets/i18n/', '.json'),
        deps: [HttpClient]
      }
    }),
    JwtModule.forRoot({
      config: {
        tokenGetter: getToken
      }
    }),
    MatDialogModule,
    AlertModule,
    NgxSpinnerModule,
    InactiveUserModule.forRoot({ timeout: AUTH_EXPIRATION_TIME, ping: INACTIVE_PING_CHECK }),
    SystemWarningsModule,
  ],
  bootstrap: [AppComponent],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'de' },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    { provide: REGION, useValue: FIREBASE_REGION },
    { provide: CURRENCY_MASK_CONFIG, useValue: CurrencyConfig },
    MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER,
    { provide: LOCALE_ID, useValue: 'de-DE' },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    { provide: USE_DATABASE_EMULATOR, useValue: environment.env === 'emulator' ? ['127.0.0.1', 9000] : undefined },
    { provide: USE_FIRESTORE_EMULATOR, useValue: environment.env === 'emulator' ? ['localhost', 8080] : undefined },
    { provide: USE_FUNCTIONS_EMULATOR, useValue: environment.env === 'emulator' ? ['localhost', 5001] : undefined },
    CookieService,
    { provide: APP_INITIALIZER, useFactory: updateBaseHref, multi: true }
  ]
})
export class AppModule {
  constructor(
    private injector: Injector,
    private dateAdapter: DateAdapter<Date>,
    private translateService: TranslateService,
    private systemWarningsService: SystemWarningsService
  ) {
    setAppInjector(this.injector);
    this.translateService.onLangChange.subscribe((result) => this.dateAdapter.setLocale(result.lang));
    this.systemWarningsService.init();
  }
}
